import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import SolarSystem from '../components/galaxy/SolarSystem';
import { FiPlusCircle, FiClock, FiCheckCircle, FiStar, FiChevronRight } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const Dashboard = () => {
  const { user, presets, addContribution } = useApp();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ presetId: '', projectId: '', amount: '', description: '' });

  useEffect(() => {
    if (presets.length > 0 && !formData.presetId) {
      setFormData(prev => ({ ...prev, presetId: presets[0].id }));
    }
  }, [presets, formData.presetId]);

  // When preset changes, reset project and populate if projects exist
  useEffect(() => {
    const selectedPreset = presets.find(p => p.id === formData.presetId);
    if (selectedPreset && selectedPreset.projects.length > 0) {
      const firstProj = selectedPreset.projects[0];
      setFormData(prev => ({
        ...prev,
        projectId: firstProj.id,
        amount: firstProj.value,
        description: firstProj.description
      }));
    } else {
      setFormData(prev => ({ ...prev, projectId: '', amount: '', description: '' }));
    }
  }, [formData.presetId, presets]);

  // When project changes, update amount and description
  const handleProjectChange = (projId) => {
    const selectedPreset = presets.find(p => p.id === formData.presetId);
    const selectedProj = selectedPreset?.projects.find(p => p.id === projId);
    if (selectedProj) {
      setFormData({
        ...formData,
        projectId: projId,
        amount: selectedProj.value,
        description: selectedProj.description
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    addContribution(formData.presetId, formData.projectId, formData.amount, formData.description);
    setShowForm(false);
  };

  if (!user) return null;
  const verifiedCount = user.contributions.filter(c => c.status === 'verified').length;
  const pendingCount = user.contributions.filter(c => c.status === 'pending').length;
  const selectedPreset = presets.find(p => p.id === formData.presetId);

  return (
    <div className="min-h-screen pt-24 px-4 pb-12 galaxy-bg">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Galaxy Preview */}
        <div className="lg:col-span-2 space-y-8">
          <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8 backdrop-blur-md flex flex-col items-center justify-center min-h-[550px] relative overflow-hidden">
            <div className="absolute inset-0 bg-blue-500/5 blur-[100px]" />
            <h2 className="text-2xl font-bold mb-8 text-slate-200 z-10 flex items-center gap-3">
              <FiStar className="text-yellow-400" /> Your Constellation
            </h2>
            <SolarSystem user={user} isDetailView={true} />
            <div className="grid grid-cols-3 gap-8 mt-12 z-10 w-full max-w-lg">
              <StatCard label="Total Stars" value={user.contributions.length} />
              <StatCard label="Active Orbits" value={verifiedCount} color="text-green-400" />
              <StatCard label="Forming" value={pendingCount} color="text-yellow-400" />
            </div>
          </div>
        </div>

        {/* Actions & History */}
        <div className="space-y-8">
          <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 backdrop-blur-md">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-white">Add Impact</h3>
              <button onClick={() => setShowForm(!showForm)} className={`p-2 rounded-lg transition-all ${showForm ? 'bg-red-500/20 text-red-400' : 'bg-blue-600 text-white hover:bg-blue-500'}`}>
                <FiPlusCircle size={24} />
              </button>
            </div>

            <AnimatePresence>
              {showForm && (
                <motion.form initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} onSubmit={handleSubmit} className="space-y-4 mb-8 overflow-hidden">
                  <div>
                    <label className="text-[10px] uppercase text-slate-500 font-bold mb-1 block">Impact Category</label>
                    <select value={formData.presetId} onChange={e => setFormData({ ...formData, presetId: e.target.value })} className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2.5 text-white focus:ring-2 focus:ring-blue-500 outline-none">
                      {presets.map(p => (
                        <option key={p.id} value={p.id}>{p.label}</option>
                      ))}
                    </select>
                  </div>

                  {selectedPreset?.projects.length > 0 ? (
                    <div>
                      <label className="text-[10px] uppercase text-slate-500 font-bold mb-1 block">Specific Project</label>
                      <select value={formData.projectId} onChange={e => handleProjectChange(e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2.5 text-white focus:ring-2 focus:ring-blue-500 outline-none">
                        {selectedPreset.projects.map(p => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-yellow-500 text-xs text-center">No projects defined for this category. Contact admin.</div>
                  )}

                  {formData.projectId && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 bg-slate-800/50 rounded-xl border border-slate-700 space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-slate-400">Standard Impact Value:</span>
                        <span className="text-sm font-bold text-white">{formData.amount} {selectedPreset?.unit}</span>
                      </div>
                      <p className="text-xs text-slate-400 leading-relaxed italic">"{formData.description}"</p>
                    </motion.div>
                  )}

                  <button type="submit" disabled={!formData.projectId} className="w-full bg-gradient-to-r from-blue-600 to-purple-600 py-3 rounded-lg font-bold hover:opacity-90 disabled:opacity-50 transition-all shadow-xl shadow-blue-600/20">Launch Star</button>
                </motion.form>
              )}
            </AnimatePresence>

            <div className="space-y-4">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Recent Signals</h4>
              <div className="space-y-3 max-h-[450px] overflow-y-auto pr-2 custom-scrollbar">
                {user.contributions.length === 0 && <p className="text-slate-500 text-sm italic">No stars launched yet.</p>}
                {[...user.contributions].reverse().map(c => {
                  const preset = presets.find(p => p.id === c.presetId);
                  return (
                    <div key={c.id} className="p-4 bg-slate-800/40 rounded-xl border border-slate-800/50 flex gap-4">
                      <div className={`w-1 rounded-full ${preset?.color || 'bg-slate-600'}`} />
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-bold text-slate-200 text-sm line-clamp-1">{c.description}</span>
                          <span className="text-[10px] text-slate-500 shrink-0">{new Date(c.date).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-[10px] font-bold text-slate-500">{c.amount} {preset?.unit}</span>
                          {c.status === 'verified' ? (
                            <span className="flex items-center text-[10px] text-green-400 font-bold"><FiCheckCircle size={10} className="mr-1" /> Verified</span>
                          ) : (
                            <span className="flex items-center text-[10px] text-yellow-500 font-bold"><FiClock size={10} className="mr-1" /> Forming</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, color = "text-white" }) => (
  <div className="text-center p-4 bg-slate-800/30 rounded-2xl border border-slate-800/50">
    <div className={`text-3xl font-black mb-1 ${color}`}>{value}</div>
    <div className="text-[10px] text-slate-500 uppercase font-black tracking-tighter">{label}</div>
  </div>
);

export default Dashboard;